import { zodResolver } from '@hookform/resolvers/zod';
import { authControllerLogin, authControllerMfaChallenge } from '@kalba/api-client';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { IdentifierInput } from '../components/ui/identifier-input';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuthStore } from '../stores/auth.store';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

const loginSchema = z.object({
  identifier: z.string().min(1, 'Email, username, or phone number is required'),
  password: z.string().min(1, 'Password is required'),
});

const mfaSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits').regex(/^\d+$/, 'Digits only'),
});

type LoginForm = z.infer<typeof loginSchema>;
type MfaForm = z.infer<typeof mfaSchema>;

const BG_IMAGES = [
  '/images/bg-kayak.jpg',
  '/images/bg-mangroves.png',
  '/images/bg-clocktower.jpg',
  '/images/bg-khor.jpg',
];

const SLIDE_DURATION = 6000;
const TRANSITION_DURATION = 1500;

const KB_ANIMS = [
  `kenburns-a ${SLIDE_DURATION + TRANSITION_DURATION}ms ease-in-out forwards`,
  `kenburns-b ${SLIDE_DURATION + TRANSITION_DURATION}ms ease-in-out forwards`,
  `kenburns-c ${SLIDE_DURATION + TRANSITION_DURATION}ms ease-in-out forwards`,
  `kenburns-d ${SLIDE_DURATION + TRANSITION_DURATION}ms ease-in-out forwards`,
];

function KenBurnsBackground() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActive((i) => (i + 1) % BG_IMAGES.length);
    }, SLIDE_DURATION);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <style>{`
        @keyframes kenburns-a {
          0%   { transform: scale(1.0) translate(0%, 0%); }
          100% { transform: scale(1.12) translate(-2%, -1%); }
        }
        @keyframes kenburns-b {
          0%   { transform: scale(1.08) translate(1%, 1%); }
          100% { transform: scale(1.0) translate(-1%, 0%); }
        }
        @keyframes kenburns-c {
          0%   { transform: scale(1.0) translate(-1%, 0%); }
          100% { transform: scale(1.1) translate(2%, 1%); }
        }
        @keyframes kenburns-d {
          0%   { transform: scale(1.1) translate(0%, -1%); }
          100% { transform: scale(1.0) translate(1%, 0%); }
        }
      `}</style>

      {BG_IMAGES.map((src, i) => (
        <div
          key={src}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: active === i ? 1 : 0,
            transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`,
            zIndex: active === i ? 1 : 0,
            animation: active === i ? KB_ANIMS[i] : 'none',
          }}
        />
      ))}

      {/* Dark overlay — above slides */}
      <div className="absolute inset-0 bg-black/45" style={{ zIndex: 2 }} />
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const { setSession, setMfaChallenge, mfaChallenge } = useAuthStore();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: '', password: '' },
  });

  const mfaForm = useForm<MfaForm>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: '' },
  });

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    try {
      const res = (await authControllerLogin({ requestBody: data })) as unknown as
        | {
            accessToken: string;
            user: {
              id: string;
              email: string;
              name: string;
              avatarUrl?: string | null;
              permissions: string[];
              mustChangePassword?: boolean;
            };
          }
        | { mfaRequired: true; challengeToken: string };

      if ('mfaRequired' in res && res.mfaRequired) {
        setMfaChallenge(res.challengeToken);
        return;
      }

      const full = res as {
        accessToken: string;
        user: {
          id: string;
          email: string;
          name: string;
          avatarUrl?: string | null;
          permissions: string[];
          licensingAuthorities?: string[];
          mustChangePassword?: boolean;
        };
      };
      setSession(
        {
          id: full.user.id,
          email: full.user.email,
          name: full.user.name,
          avatarUrl: full.user.avatarUrl,
        },
        full.user.permissions,
        full.accessToken,
        full.user.mustChangePassword,
        full.user.licensingAuthorities ?? [],
      );
      if (full.user.mustChangePassword) {
        navigate({ to: '/change-password' });
      } else {
        navigate({ to: '/dashboard' });
      }
    } catch {
      setServerError('Invalid email or password');
    }
  };

  const onMfaSubmit = async (data: MfaForm) => {
    setServerError(null);
    try {
      const res = (await authControllerMfaChallenge({
        requestBody: { challengeToken: mfaChallenge!.challengeToken, code: data.code },
      })) as unknown as {
        accessToken: string;
        user: {
          id: string;
          email: string;
          name: string;
          avatarUrl?: string | null;
          permissions: string[];
          licensingAuthorities?: string[];
        };
      };

      setSession(
        {
          id: res.user.id,
          email: res.user.email,
          name: res.user.name,
          avatarUrl: res.user.avatarUrl,
        },
        res.user.permissions,
        res.accessToken,
        false,
        res.user.licensingAuthorities ?? [],
      );
      navigate({ to: '/dashboard' });
    } catch {
      setServerError('Invalid or expired code. Try again.');
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-end overflow-hidden">
      <KenBurnsBackground />

      {/* Login card */}
      <div className="relative z-10 w-full min-h-screen flex items-center justify-center px-4 py-8 sm:px-8 xl:justify-end xl:pr-16 2xl:pr-24">
        <div className="w-full max-w-[440px] xl:max-w-[480px] rounded-2xl bg-white/97 backdrop-blur-sm shadow-2xl px-8 sm:px-10 py-10">
          {/* Logo */}
          <div className="mb-6 flex items-center gap-4">
            <img
              src="/images/Kalba-Logo-Orange.svg"
              alt="Kalba Municipality"
              className="h-auto w-[150px] object-contain"
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
              Istisharat
            </h1>
          </div>

          <hr className="mb-7 border-gray-200" />

          {mfaChallenge ? (
            <>
              <div className="mb-6">
                <h2 className="text-xs font-semibold text-gray-800">Two-factor authentication</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              <form
                onSubmit={mfaForm.handleSubmit(onMfaSubmit)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
                    e.preventDefault();
                    mfaForm.handleSubmit(onMfaSubmit)();
                  }
                }}
                className="space-y-4"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="code">Authenticator code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    className="text-center tracking-widest text-lg"
                    {...mfaForm.register('code')}
                  />
                  {mfaForm.formState.errors.code && (
                    <p className="text-xs text-red-600">{mfaForm.formState.errors.code.message}</p>
                  )}
                </div>
                {serverError && (
                  <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    {serverError}
                  </div>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={mfaForm.formState.isSubmitting}
                >
                  {mfaForm.formState.isSubmitting ? 'Verifying...' : 'Verify'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    useAuthStore.getState().clearMfaChallenge();
                    setServerError(null);
                  }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Back to login
                </button>
              </form>
            </>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
                  e.preventDefault();
                  handleSubmit(onSubmit)();
                }
              }}
              className="space-y-6"
            >
              <div className="space-y-1.5">
                <Label htmlFor="identifier">
                  Email, Username, or Phone Number <span className="text-red-500">*</span>
                </Label>
                <Controller
                  control={control}
                  name="identifier"
                  render={({ field }) => (
                    <IdentifierInput
                      id="identifier"
                      ref={field.ref}
                      onValueChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Enter your email, username, or phone number"
                      autoComplete="username"
                      className="h-11"
                      hasError={!!errors.identifier}
                    />
                  )}
                />
                {errors.identifier && (
                  <p className="text-xs text-red-600">{errors.identifier.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">
                  Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className="h-11 pr-10"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  Forgot Password?
                </Link>
              </div>

              {serverError && (
                <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  {serverError}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full font-semibold"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}

          {/* Powered by re.life */}
          <div className="mt-8 flex items-center justify-center gap-1.5">
            <span className="text-[11px] text-gray-400">Powered by</span>
            <img
              src="/images/ReLife LogoBlack.png"
              alt="re.life"
              className="h-4 w-auto object-contain opacity-55"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
